import React, { memo, useCallback, useContext, useMemo } from 'react'

import {
  Button,
  Icon,
  ArrowsExchange,
} from '@cgtrader/triangle-ui/dist/src/ssr-index'

import { iconSize } from 'Pods/ThreeDStudio/constants'
import useDccBridgeAccess from 'Pods/ThreeDStudio/Hooks/useDccBridgeAccess'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'
import { isDccFirstUseFor } from 'Pods/ThreeDStudio/Services/dccBridgeFirstUse'
import { STUDIO_EVENT, trackStudioEvent } from 'Pods/ThreeDStudio/Services/studioTracker'
import { useThreeDStudioStore } from 'Pods/ThreeDStudio/Store'
import {
  openSendToModal,
  sendModelToDcc,
} from 'Pods/ThreeDStudio/Store/actions/dccBridge'
import { openPaidFeatureModal } from 'Pods/ThreeDStudio/Store/actions/ui'
import { DCC_SEND_STATUS } from 'Pods/ThreeDStudio/Store/slices/dccBridge'
import ThreeDStudioPageContext from 'Pods/ThreeDStudio/ThreeDStudioPageContext'

const sendIcon = <Icon svgComponent={ArrowsExchange} color="dark" size={iconSize} notSpaced />

const IN_FLIGHT_STATUSES = new Set([DCC_SEND_STATUS.CONNECTING, DCC_SEND_STATUS.SENDING])

function SendToButton() {
  const t = useTranslation()
  const hasDccBridgeAccess = useDccBridgeAccess()
  const { dccSoftwares: softwares } = useContext(ThreeDStudioPageContext)
  const selectedName = useThreeDStudioStore(state => state.dccBridge.selectedSoftwareName)
  const sendStatus = useThreeDStudioStore(state => state.dccBridge.sendStatus)

  const selectedSoftware = useMemo(() => {
    if (!selectedName) return null
    return softwares.find(
      s => s.name === selectedName && s.isEnabled,
    ) || null
  }, [softwares, selectedName])

  const isSendInFlight = IN_FLIGHT_STATUSES.has(sendStatus)
  // When locked the button stays enabled so clicking it opens the upsell; the
  // send-specific guards (a software must be selected, no send in flight) only
  // apply once the user actually has access.
  const isDisabled = hasDccBridgeAccess && (!selectedSoftware || isSendInFlight)

  const handleSendClick = useCallback(() => {
    if (!hasDccBridgeAccess) {
      trackStudioEvent(STUDIO_EVENT.DCC_BRIDGE_CLICK, {
        action: 'send',
        software: selectedSoftware?.name ?? null,
        locked: true,
      })
      openPaidFeatureModal()
      return
    }
    if (isDisabled) return
    trackStudioEvent(STUDIO_EVENT.DCC_BRIDGE_CLICK, {
      action: 'send',
      software: selectedSoftware.name,
      locked: false,
    })
    if (isDccFirstUseFor(selectedSoftware.name)) {
      openSendToModal()
    } else {
      sendModelToDcc(selectedSoftware.name)
    }
  }, [hasDccBridgeAccess, isDisabled, selectedSoftware])

  const label = selectedSoftware
    ? t('footerActions.sendTo.labelWithName', { name: selectedSoftware.name })
    : t('footerActions.sendTo.label')

  return (
    <Button
      icon={sendIcon}
      type="secondary"
      size="small"
      variant="with-icon"
      aria-label={selectedSoftware ? `Send to ${selectedSoftware.name}` : 'Send to'}
      disabled={isDisabled}
      onClick={handleSendClick}
      customClass="dcc-bridge-cluster__send"
    >
      {label}
    </Button>
  )
}

export default memo(SendToButton)
